import {
  DayOfWeek,
  TypeGuard,
  isDocRef,
  isObject,
  type DocumentReference,
} from '@principle-theorem/shared';
import { isBoolean, isString } from 'lodash';
import { type ISchedulingEventReason } from '../appointment/cancellation';
import { DepositBookingContext } from './deposit-booking-context';
import { DepositCaptureStrategy } from './deposit-capture-strategy';

/**
 * Discriminator for deposit amount calculation. Only fixed cents is supported;
 * the member is retained so the persisted shape can carry another calculation
 * basis later without a migration. Persisted in Firestore treatment-template
 * documents.
 */
export enum DepositAmountType {
  Fixed = 'fixed',
}

export interface IDepositAmount {
  type: DepositAmountType;
  amountCents: number;
}

export interface IDepositForfeitureRule {
  reasonRef: DocumentReference<ISchedulingEventReason>;
  forfeiturePercentage: number;
}

export interface IDepositTreatmentConfiguration {
  amountCents: number;
}

export interface IDepositTreatmentConfigurationContribution
  extends IDepositTreatmentConfiguration {
  quantity: number;
}

export function isDepositTreatmentConfigurationContributionMap(
  data: unknown
): data is Record<string, IDepositTreatmentConfigurationContribution> {
  return (
    isObject(data) &&
    !Array.isArray(data) &&
    Object.values(data).every((deposit) =>
      TypeGuard.interface<IDepositTreatmentConfigurationContribution>({
        amountCents: TypeGuard.positiveInteger,
        quantity: TypeGuard.positiveInteger,
      })(deposit)
    )
  );
}

export enum DepositForfeiturePolicy {
  Auto = 'auto',
  Manual = 'manual',
}

/**
 * Who a forfeited deposit's Cancellation Fee is credited to. `Practice` leaves
 * the fee unattributed, so income reporting counts it as unallocated rather
 * than as a practitioner's earnings.
 */
export enum CancellationFeeAttribution {
  Practice = 'practice',
  Practitioner = 'practitioner',
}

export interface IDepositSettings {
  /**
   * Master switch for the deposits feature. When false no deposit is ever
   * required, regardless of treatment template or patient configuration. The
   * remaining settings are retained so re-enabling restores them.
   */
  enabled: boolean;
  /**
   * The booking contexts a deposit applies to. Acts as the blanket rule: a
   * Treatment Template that requires a deposit only does so when the booking's
   * context appears in this list, unless the template sets its own `appliesTo`.
   *
   * Absent means all contexts, matching the behaviour before this setting
   * existed. The settings form always writes an explicit non-empty list.
   */
  appliesTo?: DepositBookingContext[];
  /**
   * Brand-level timing for when deposit money is taken. This stores the same
   * axis as a Treatment Template override so the effective strategy can be
   * resolved without widening from a provider-facing payment mode. A template's
   * `captureStrategy` overrides this value when its deposit is required.
   */
  captureStrategy: DepositCaptureStrategy;
  forfeitureRules: IDepositForfeitureRule[];
  forfeiturePolicy: DepositForfeiturePolicy;
  /**
   * Who a forfeited deposit's Cancellation Fee is credited to by default.
   * Staff with the deposit override permission can change it per
   * cancellation. Absent means the practice.
   */
  cancellationFeeAttribution?: CancellationFeeAttribution;
  forfeitureEmailEnabled: boolean;
  refundConfirmationEmailEnabled: boolean;
  /**
   * Public URL to the brand's cancellation policy. Substituted into the deposit
   * forfeit notice email under the `{{ cancellationPolicyUrl }}` placeholder.
   * When absent the placeholder resolves to an empty string so no raw
   * `{{ cancellationPolicyUrl }}` token leaks into the rendered email.
   */
  cancellationPolicyUrl?: string;
  /**
   * When true, matching appointment booking deposits are automatically applied
   * at checkout before other payment options. Defaults to false when absent.
   */
  autoApplyAppointmentAccountCredits?: boolean;
  /**
   * Cents taken when a patient is flagged deposit-required and the Treatment
   * Template sets no amount. Fixed rather than {@link IDepositAmount} because
   * it must resolve without a treatment price.
   */
  patientDepositAmountCents?: number;
  treatmentTemplateOverrides?: Record<string, IDepositTreatmentOverride>;
  treatmentConfigurationDeposits?: Record<
    string,
    IDepositTreatmentConfiguration
  >;
}

export type IDepositTreatmentOverride =
  | {
      depositRequired: false;
    }
  | {
      depositRequired: true;
      amount: IDepositAmount;
      captureStrategy?: DepositCaptureStrategy;
      /**
       * Replaces `IDepositSettings.appliesTo` for this Treatment Template.
       * Absent means inherit the blanket rule; a non-empty list fully replaces
       * it and may narrow or broaden which contexts require the deposit.
       */
      appliesTo?: DepositBookingContext[];
      /**
       * Appointment weekdays on which this Treatment Template requires its
       * deposit. Absent means every day for compatibility with existing
       * overrides.
       */
      depositRequiredDays?: DayOfWeek[];
    };

export const isDepositAmount = TypeGuard.interface<IDepositAmount>({
  type: TypeGuard.enumValue(DepositAmountType),
  amountCents: TypeGuard.positiveInteger,
});

export const isDepositForfeitureRule =
  TypeGuard.interface<IDepositForfeitureRule>({
    reasonRef: isDocRef,
    forfeiturePercentage: (data): data is number =>
      TypeGuard.positiveInteger(data) && data <= 100,
  });

export function isDepositTreatmentConfigurationMap(
  data: unknown
): data is Record<string, IDepositTreatmentConfiguration> {
  return (
    isObject(data) &&
    !Array.isArray(data) &&
    Object.values(data).every((deposit) =>
      TypeGuard.interface<IDepositTreatmentConfiguration>({
        amountCents: TypeGuard.positiveInteger,
      })(deposit)
    )
  );
}

function isDepositBookingContextList(
  data: unknown
): data is DepositBookingContext[] {
  return (
    Array.isArray(data) &&
    data.length > 0 &&
    data.every(TypeGuard.enumValue(DepositBookingContext))
  );
}

export const isDepositSettings = TypeGuard.interface<IDepositSettings>({
  enabled: isBoolean,
  appliesTo: TypeGuard.nilOr(isDepositBookingContextList),
  captureStrategy: TypeGuard.enumValue(DepositCaptureStrategy),
  forfeitureRules: TypeGuard.arrayOf(isDepositForfeitureRule),
  forfeiturePolicy: TypeGuard.enumValue(DepositForfeiturePolicy),
  cancellationFeeAttribution: TypeGuard.nilOr(
    TypeGuard.enumValue(CancellationFeeAttribution)
  ),
  forfeitureEmailEnabled: isBoolean,
  refundConfirmationEmailEnabled: isBoolean,
  cancellationPolicyUrl: TypeGuard.nilOr(isString),
  autoApplyAppointmentAccountCredits: TypeGuard.nilOr(isBoolean),
  patientDepositAmountCents: TypeGuard.nilOr(TypeGuard.positiveInteger),
  treatmentTemplateOverrides: TypeGuard.nilOr(
    (data): data is Record<string, IDepositTreatmentOverride> =>
      isObject(data) &&
      !Array.isArray(data) &&
      Object.values(data).every(isDepositTreatmentOverride)
  ),
  treatmentConfigurationDeposits: TypeGuard.nilOr(
    isDepositTreatmentConfigurationMap
  ),
});

export function isDepositTreatmentOverride(
  data: unknown
): data is IDepositTreatmentOverride {
  if (!isObject(data) || !isBoolean(data.depositRequired)) {
    return false;
  }
  if (!data.depositRequired) {
    return data.amount === undefined;
  }
  return (
    isDepositAmount(data.amount) &&
    TypeGuard.nilOr(TypeGuard.enumValue(DepositCaptureStrategy))(
      data.captureStrategy
    ) &&
    TypeGuard.nilOr(isDepositBookingContextList)(data.appliesTo) &&
    TypeGuard.nilOr(
      (days): days is DayOfWeek[] =>
        TypeGuard.arrayOf(TypeGuard.enumValue(DayOfWeek))(days) &&
        days.length > 0
    )(data.depositRequiredDays)
  );
}
